import {
    getItemId,
    isField,
    isMetric,
    type ApiCalculateTotalResponse,
    type ApiError,
    type CalculateTotalFromQuery,
    type DashboardFilters,
    type ItemsMap,
    type MetricQuery,
    type MetricQueryRequest,
    type ParametersValuesMap,
} from '@lightdash/common';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { lightdashApi } from '../api';
import {
    convertDateDashboardFilters,
    convertDateFilters,
} from '../utils/dateFilter';
import { useProjectUuid } from './useProjectUuid';

const calculateTotalFromQuery = async ({
    projectUuid,
    metricQuery,
    explore,
    invalidateCache,
    parameters,
}: {
    projectUuid: string;
    metricQuery?: MetricQuery;
    explore?: string;
    invalidateCache?: boolean;
    parameters?: ParametersValuesMap;
}): Promise<ApiCalculateTotalResponse['results']> => {
    if (!metricQuery || !explore) {
        throw new Error(
            'missing metricQuery or explore on calculateTotalFromQuery',
        );
    }

    const timezoneFixPayload: CalculateTotalFromQuery = {
        explore: explore,
        metricQuery: {
            ...metricQuery,
            filters: convertDateFilters(metricQuery.filters),
        },
        parameters,
        invalidateCache,
    };
    return lightdashApi<ApiCalculateTotalResponse['results']>({
        url: `/projects/${projectUuid}/calculate-total`,
        method: 'POST',
        body: JSON.stringify(timezoneFixPayload),
    });
};

const calculateTotalFromSavedChart = async ({
    savedChartUuid,
    dashboardFilters,
    invalidateCache,
    parameters,
}: {
    savedChartUuid: string;
    dashboardFilters?: DashboardFilters;
    invalidateCache?: boolean;
    parameters?: ParametersValuesMap;
}): Promise<ApiCalculateTotalResponse['results']> => {
    const timezoneFixFilters =
        dashboardFilters && convertDateDashboardFilters(dashboardFilters);

    return lightdashApi<ApiCalculateTotalResponse['results']>({
        url: `/saved/${savedChartUuid}/calculate-total`,
        method: 'POST',
        body: JSON.stringify({
            dashboardFilters: timezoneFixFilters,
            invalidateCache,
            parameters,
        }),
    });
};

const postCalculateTotalForEmbed = async ({
    embedToken,
    projectUuid,
    savedChartUuid,
    dashboardFilters,
    invalidateCache,
}: {
    embedToken: string;
    projectUuid: string;
    savedChartUuid: string;
    dashboardFilters?: DashboardFilters;
    invalidateCache?: boolean;
}): Promise<ApiCalculateTotalResponse['results']> => {
    const timezoneFixFilters =
        dashboardFilters && convertDateDashboardFilters(dashboardFilters);

    return lightdashApi<ApiCalculateTotalResponse['results']>({
        url: `/embed/${projectUuid}/chart/${savedChartUuid}/calculate-total`,
        method: 'POST',
        headers: {
            'Lightdash-Embed-Token': embedToken,
        },
        body: JSON.stringify({
            dashboardFilters: timezoneFixFilters,
            invalidateCache,
        }),
    });
};

/**
 * Calculate totals from a raw metric query in embed context.
 * Used when exploring data directly (not from a saved chart).
 */
const postCalculateTotalFromQueryForEmbed = async ({
    embedToken,
    projectUuid,
    metricQuery,
    explore,
    invalidateCache,
    parameters,
}: {
    embedToken: string;
    projectUuid: string;
    metricQuery: MetricQuery;
    explore: string;
    invalidateCache?: boolean;
    parameters?: ParametersValuesMap;
}): Promise<ApiCalculateTotalResponse['results']> => {
    const timezoneFixPayload: CalculateTotalFromQuery = {
        explore,
        metricQuery: {
            ...metricQuery,
            filters: convertDateFilters(metricQuery.filters),
        },
        parameters,
        invalidateCache,
    };
    return lightdashApi<ApiCalculateTotalResponse['results']>({
        url: `/embed/${projectUuid}/calculate-total`,
        method: 'POST',
        headers: {
            'Lightdash-Embed-Token': embedToken,
        },
        body: JSON.stringify(timezoneFixPayload),
    });
};

const getCalculationColumnFields = (
    selectedItemIds: string[],
    itemsMap: ItemsMap,
) => {
    //This method will return the metric ids that need to be calculated in the backend

    const items = selectedItemIds
        ?.map((item) => {
            return itemsMap[item];
        })
        .filter((item) => isField(item) && isMetric(item));

    return items?.reduce<string[]>((acc, item) => {
        if (isField(item)) return [...acc, getItemId(item)];
        return acc;
    }, []);
};

export const useCalculateTotal = ({
    metricQuery,
    explore,
    savedChartUuid,
    fieldIds,
    dashboardFilters,
    invalidateCache,
    itemsMap,
    showColumnCalculation,
    embedToken,
    parameters,
}: {
    metricQuery?: MetricQueryRequest;
    explore?: string;
    savedChartUuid?: string;
    dashboardFilters?: DashboardFilters;
    invalidateCache?: boolean;
    itemsMap: ItemsMap | undefined;
    fieldIds?: string[];
    showColumnCalculation?: boolean;
    embedToken: string | undefined;
    parameters?: ParametersValuesMap;
}) => {
    const metricsWithTotals = useMemo(() => {
        if (!fieldIds || !itemsMap) return [];
        if (showColumnCalculation === false) return [];
        return getCalculationColumnFields(fieldIds, itemsMap);
    }, [fieldIds, itemsMap, showColumnCalculation]);
    const projectUuid = useProjectUuid();

    // only add relevant fields to the key (filters, metrics)
    const queryKey = savedChartUuid
        ? { savedChartUuid, dashboardFilters, invalidateCache, parameters }
        : {
              filters: metricQuery?.filters,
              metrics: metricQuery?.metrics,
              additionalMetrics: metricQuery?.additionalMetrics,
              invalidateCache,
              parameters,
          };

    return useQuery<ApiCalculateTotalResponse['results'], ApiError>({
        queryKey: ['calculate_total', projectUuid, queryKey],
        queryFn: () =>
            // Embed mode with saved chart
            embedToken && projectUuid && savedChartUuid
                ? postCalculateTotalForEmbed({
                      embedToken,
                      projectUuid,
                      savedChartUuid,
                      dashboardFilters,
                      invalidateCache,
                  })
                : // Embed mode with raw query (Explore)
                  embedToken && projectUuid && metricQuery && explore
                  ? postCalculateTotalFromQueryForEmbed({
                        embedToken,
                        projectUuid,
                        metricQuery,
                        explore,
                        invalidateCache,
                        parameters,
                    })
                  : // Regular mode with saved chart
                    savedChartUuid
                    ? calculateTotalFromSavedChart({
                          savedChartUuid,
                          dashboardFilters,
                          invalidateCache,
                          parameters,
                      })
                    : // Regular mode with raw query
                      projectUuid
                      ? calculateTotalFromQuery({
                            projectUuid,
                            metricQuery,
                            explore,
                            invalidateCache,
                            parameters,
                        })
                      : Promise.reject(),
        retry: false,
        enabled:
            metricsWithTotals.length > 0 &&
            (metricQuery || savedChartUuid) !== undefined,
        onError: (result) =>
            console.error(
                `Unable to calculate total from query: ${
                    result?.error?.message || result
                }`,
            ),
    });
};
